/**
 * Public-viewer live-device hook for `/event/<id>`.
 *
 * Joins three sources into a single `EventLiveDevice[]` keyed by
 * device_id:
 *   1. `/api/fleet/events/<id>` — the event row (gates, public flag, name)
 *   2. `/api/fleet/events/<id>/enrollments?state=armed` — the device-ID
 *      allowlist for this event. Pins are filtered to this set so a
 *      foreign device's telemetry never bleeds into the wrong event.
 *   3. `/api/fleet/map/devices` — baseline GPS snapshot, then live
 *      `device_telemetry` SSE frames merged on top.
 *
 * The armed roster (2) is the SPINE: every armed enrollment gets a row
 * whether or not it has a position, and GPS from (3) is overlaid onto
 * it. Building the list from GPS instead meant an armed, recording
 * phone that had not yet produced a fix was absent from the viewer
 * entirely — no row, no explanation — which read to spectators and
 * organizers alike as "the car isn't in the event". `has_fix` is what
 * every map surface must gate on; `lat`/`lon` are 0 while it is false.
 *
 * This reuses the existing `/api/events/stream` SSE — no new server
 * endpoint. First paint doesn't block on the SSE open; the event +
 * enrollment + map fetches race in parallel and pins appear as soon
 * as the snapshot lands.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { normalizeBatteryFraction } from '@/lib/utils';
import api from '../services/api';
import * as fleetApi from '../services/fleet';
import { FleetEvent, MapDevice } from '../types';
import { EnrollmentRow, EventRow, Run } from '../services/fleet';

export interface EventLiveDevice extends MapDevice {
  /** Driver name from the enrollment row (operator-entered at join). */
  driver_name: string | null;
  /** Vehicle string from the enrollment row. */
  vehicle: string | null;
  /** Numeric enrollment id. Identity, selection and follow stay keyed on
   *  this — `car_number` is display-only. */
  enrollment_id: number;
  /** Organizer-assigned race number ("12", "12A") — null falls back to
   *  the enrollment id for display (see carNumber.ts). */
  car_number: string | null;
  /** Session mounted in the player. Sticky across a disconnect — only a
   *  telemetry frame with a different id, an explicit null, or the runs
   *  list reporting this id no longer recording changes it. Not itself
   *  a liveness signal; see `isLiveNow`. */
  session_id: string | null;
  /** `Date.now()` at the last telemetry frame or `device_online` event.
   *  Null until one arrives (e.g. a device built from the replay branch,
   *  which never runs the SSE merge). Feeds `isLiveNow`'s grace window
   *  for a device that's `online: false` but may just be mid-reconnect. */
  last_seen_ms: number | null;
  /** Latest IMU accel-magnitude peak (m/s², gravity included) from the
   *  1 s telemetry summary — feeds the fullscreen glass rail's G cell.
   *  Null until the first V2 IMU frame (legacy phones never send it). */
  accel_peak_mps2: number | null;
  /** False until this device has produced a GPS fix from any source.
   *  `lat`/`lon` are 0 in that state, so anything that plots a position
   *  (map pins, mini-map, map centering) must skip the row. */
  has_fix: boolean;
}

export interface EventLiveDevicesResult {
  event: EventRow | null;
  devices: EventLiveDevice[];
  /** True until the initial event + enrollment + map fetches resolve. */
  loading: boolean;
  /** Non-null when the event 404s or the enrollment list fails. */
  error: string | null;
  /** True when the event's active window has passed — the page renders
   *  recorded runs (VOD) instead of the live armed-enrollment feed. */
  replay: boolean;
}

const POLL_INTERVAL_MS = 30_000;

// The viewer's own liveness window, independent of the backend's
// abandoned-session sweep (which ends a dead row only after hours). This
// decides the LIVE badge while a phone may still be mid-reconnect.
const STALE_LIVE_MS = 120_000;

/** Whether a device's mounted session should present as LIVE right now
 *  (badge, live count, live filter, CarsTab sort) — distinct from
 *  `session_id != null`, which only says a session is mounted and must
 *  stay stable across a reconnect. A device that just went offline keeps
 *  reading as live for `STALE_LIVE_MS` in case it's mid-reconnect. */
export function isLiveNow(device: EventLiveDevice, nowMs: number): boolean {
  if (device.session_id == null) return false;
  if (device.online) return true;
  return device.last_seen_ms != null && nowMs - device.last_seen_ms < STALE_LIVE_MS;
}

type FixFields = Pick<
  MapDevice,
  'lat' | 'lon' | 'accuracy_m' | 'speed_mps' | 'heading_deg' | 'fix_ts_ms'
>;

/** Newest fix across the two GPS sources, or null when neither has one.
 *  `/api/fleet/map/devices` only ever returns rows that have a fix, so a
 *  missing baseline means "no persisted position", not "stale". */
function pickFix(
  baseline: MapDevice | undefined,
  live: EventLiveDevice | undefined,
): FixFields | null {
  const liveFix = live?.has_fix ? live : undefined;
  let best: MapDevice | undefined;
  if (liveFix && baseline) {
    best = (liveFix.fix_ts_ms ?? 0) >= (baseline.fix_ts_ms ?? 0) ? liveFix : baseline;
  } else {
    best = liveFix ?? baseline;
  }
  if (!best) return null;
  return {
    lat: best.lat,
    lon: best.lon,
    accuracy_m: best.accuracy_m ?? null,
    speed_mps: best.speed_mps ?? null,
    heading_deg: best.heading_deg ?? null,
    fix_ts_ms: best.fix_ts_ms ?? null,
  };
}

/** One rail row per armed enrollment, with whatever position and live
 *  state exist layered on. Labels always come from the enrollment — it
 *  is the only source that has them for a device with no GPS at all. */
function buildDevice(
  enrollment: EnrollmentRow,
  baseline: MapDevice | undefined,
  live: EventLiveDevice | undefined,
): EventLiveDevice {
  const fix = pickFix(baseline, live);
  return {
    device_id: enrollment.device_id,
    name: live?.name ?? baseline?.name ?? null,
    kind: 'phone',
    online: live?.online ?? baseline?.online ?? false,
    lat: fix?.lat ?? 0,
    lon: fix?.lon ?? 0,
    accuracy_m: fix?.accuracy_m ?? null,
    speed_mps: fix?.speed_mps ?? null,
    heading_deg: fix?.heading_deg ?? null,
    fix_ts_ms: fix?.fix_ts_ms ?? null,
    battery_level: live?.battery_level ?? baseline?.battery_level ?? null,
    battery_charging: live?.battery_charging ?? baseline?.battery_charging ?? null,
    driver_name: enrollment.driver_name,
    vehicle: enrollment.vehicle,
    enrollment_id: enrollment.id,
    car_number: enrollment.car_number ?? null,
    session_id: live?.session_id ?? null,
    last_seen_ms: live?.last_seen_ms ?? null,
    accel_peak_mps2: live?.accel_peak_mps2 ?? null,
    has_fix: fix != null,
  };
}

export function useEventLiveDevices(
  eventId: number | null,
  runs: Run[],
): EventLiveDevicesResult {
  const [event, setEvent] = useState<EventRow | null>(null);
  const [armedById, setArmedById] = useState<Map<string, EnrollmentRow>>(new Map());
  const [devices, setDevices] = useState<EventLiveDevice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // null = unknown (event row not fetched yet). The live-only effects
  // below (map poll + SSE merge) attach only once this resolves to
  // `false`; `true` switches the hook to the one-shot replay build.
  const [replay, setReplay] = useState<boolean | null>(null);

  const liveByIdRef = useRef<Map<string, EventLiveDevice>>(new Map());
  const baselineByIdRef = useRef<Map<string, MapDevice>>(new Map());
  const armedSetRef = useRef<Set<string>>(new Set());
  const armedRowsRef = useRef<Map<string, EnrollmentRow>>(new Map());
  // device_id → session_id the runs list marked not-recording. A phone
  // that keeps reporting this id (its own state is stale, or an operator
  // force-ended the session server-side) must not remount it.
  const endedSessionIdsRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    armedSetRef.current = new Set(armedById.keys());
    armedRowsRef.current = armedById;
  }, [armedById]);

  /** Re-derive the whole list from the three refs. Every live-mode
   *  update funnels through here so the roster spine can't be bypassed
   *  by a code path that only knows about GPS. */
  const rebuild = useCallback(() => {
    const out: EventLiveDevice[] = [];
    for (const [deviceId, enrollment] of armedRowsRef.current) {
      out.push(buildDevice(
        enrollment,
        baselineByIdRef.current.get(deviceId),
        liveByIdRef.current.get(deviceId),
      ));
    }
    setDevices(out);
  }, []);

  useEffect(() => {
    if (eventId == null) return;
    let cancelled = false;
    // Reset every per-event ref + state synchronously on navigation so
    // a foreign device from the previous event can't survive into the
    // new one. Without this, three race windows leak between
    // `/event/A` → `/event/B`:
    //   1. liveByIdRef still holds event-A devices; the map-poll
    //      merge loop unconditionally re-inserts any entry whose
    //      fix_ts_ms beats the baseline, so spectators see A's
    //      drivers on B's map until refresh.
    //   2. armedSetRef still holds event-A's allowlist, so SSE
    //      device_telemetry frames for event-A devices that fire
    //      between the route change and the new fetch resolving get
    //      accepted into liveByIdRef and surface as event-B pins.
    //   3. armedRowsRef.driver_name leaks A's metadata into the
    //      panel for whatever pin the operator clicks during that
    //      window.
    // Wiping all three refs + the matching React state up-front
    // closes all three windows deterministically; the new fetch
    // repopulates them.
    liveByIdRef.current = new Map();
    baselineByIdRef.current = new Map();
    armedSetRef.current = new Set();
    armedRowsRef.current = new Map();
    endedSessionIdsRef.current = new Map();
    setEvent(null);
    setArmedById(new Map());
    setDevices([]);
    setLoading(true);
    setError(null);
    setReplay(null);

    void (async () => {
      try {
        const [eventRow, armedEnrollments] = await Promise.all([
          fleetApi.getEvent(eventId),
          fleetApi.listEnrollments(eventId, 'armed'),
        ]);
        if (cancelled) return;
        setEvent(eventRow);
        // Open-ended events (active_until null) never flip to replay.
        const endedAt = eventRow.active_until ? Date.parse(eventRow.active_until) : NaN;
        const ended = Number.isFinite(endedAt) && endedAt < Date.now();
        if (!ended) {
          const byId = new Map<string, EnrollmentRow>();
          for (const e of armedEnrollments) {
            byId.set(e.device_id, e);
          }
          setArmedById(byId);
          setReplay(false);
          return;
        }
        // Replay: the event-end sweep has disarmed every enrollment, so
        // the armed allowlist is structurally empty after active_until.
        // Build the roster from recorded runs instead (joined with
        // enrollment labels), with pins at each device's last-known GPS
        // snapshot. One-shot — no SSE merge, no 30 s poll.
        const [runs, mapRows] = await Promise.all([
          fleetApi.listEventRuns(eventId),
          api.listMapDevices(),
        ]);
        if (cancelled) return;
        const gpsByDevice = new Map(mapRows.map((r) => [r.device_id, r]));
        // Runs arrive newest-first; the first run per device is its most
        // recent, and its last constituent session is what the detail
        // panel should open with.
        const built = new Map<string, EventLiveDevice>();
        for (const run of runs) {
          if (built.has(run.device_id)) continue;
          // A run with no GPS still has footage — it gets a row with
          // `has_fix: false` and simply isn't drawn on the map.
          const gps = gpsByDevice.get(run.device_id);
          const lastSession = run.sessions[run.sessions.length - 1];
          built.set(run.device_id, {
            ...(gps ?? {
              device_id: run.device_id,
              name: null,
              kind: 'phone',
              lat: 0,
              lon: 0,
            }),
            online: false,
            driver_name: run.driver_name ?? null,
            vehicle: run.vehicle ?? null,
            enrollment_id: run.enrollment_id ?? 0,
            car_number: run.car_number ?? null,
            session_id: lastSession?.session_id ?? null,
            last_seen_ms: null,
            accel_peak_mps2: null,
            has_fix: gps != null,
          });
        }
        setDevices(Array.from(built.values()));
        setReplay(true);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'failed to load event');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  useEffect(() => {
    if (eventId == null) return;
    // Live mode only — replay (and the pre-fetch unknown state) must not
    // run the poll: its empty-allowlist branch would wipe the replay
    // roster built above.
    if (replay !== false) return;
    if (armedById.size === 0) {
      // No armed phones yet → nothing to show. Skip the map poll so we
      // don't display unrelated devices from other events.
      setDevices([]);
      liveByIdRef.current.clear();
      baselineByIdRef.current.clear();
      return;
    }
    let cancelled = false;
    // Roster first: a phone that just armed gets its row now, not a
    // network round-trip later, and keeps it if it never gets a fix.
    rebuild();

    const poll = async () => {
      try {
        const rows = await api.listMapDevices();
        if (cancelled) return;
        // Gate on armed membership: the eventId reset above already
        // wipes this on navigation, but filtering here means a stale
        // frame for a foreign device can't bleed into the baseline.
        baselineByIdRef.current = new Map(
          rows.filter((d) => armedRowsRef.current.has(d.device_id))
            .map((d) => [d.device_id, d]),
        );
        rebuild();
      } catch {
        // Map endpoint hiccups shouldn't take the whole page down.
      }
    };
    void poll();
    const id = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [eventId, armedById, replay, rebuild]);

  // `runs` is owned by the caller (shared with BrowseStateView — no poll of
  // our own). There is no backend sweep that ends an abandoned session on
  // its own: `state` only leaves "recording" via the phone's own next
  // heartbeat, its `/end` call, or an operator force-end. A phone that goes
  // dark and does none of those keeps `state: "recording"` forever — that
  // case is what `isLiveNow`'s freshness window is for, not this effect.
  useEffect(() => {
    if (eventId == null) return;
    if (replay !== false) return;
    let changed = false;
    for (const run of runs) {
      const live = liveByIdRef.current.get(run.device_id);
      if (!live?.session_id) continue;
      const endedSession = run.sessions.find(
        (s) => s.session_id === live.session_id && s.state !== 'recording',
      );
      if (endedSession) {
        endedSessionIdsRef.current.set(run.device_id, endedSession.session_id);
        liveByIdRef.current.set(run.device_id, { ...live, session_id: null });
        changed = true;
      }
    }
    if (changed) rebuild();
  }, [eventId, replay, runs, rebuild]);

  useEffect(() => {
    if (eventId == null) return;
    // Replay pages have no live telemetry to merge — skip the SSE
    // subscription entirely. (Attaching only after the event row
    // resolves loses nothing in live mode: frames are gated on the
    // armed allowlist, which is empty until that same fetch lands.)
    if (replay !== false) return;
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (evt.type === 'device_online' || evt.type === 'device_offline') {
        const enrollment = armedRowsRef.current.get(evt.device_id);
        if (!enrollment) return;
        const online = evt.type === 'device_online';
        // Connect/disconnect is the only liveness signal a phone with
        // no GPS ever produces — without it such a car sits at "idle"
        // until the 30 s map poll, which can't see it at all.
        const prior =
          liveByIdRef.current.get(evt.device_id)
          ?? buildDevice(enrollment, baselineByIdRef.current.get(evt.device_id), undefined);
        // Connectivity is independent of session identity: a reconnect
        // keeps whatever session_id was last known, so a patchy link
        // doesn't fall back to an older recording and remount the player.
        if (liveByIdRef.current.has(evt.device_id) && prior.online === online) {
          return;
        }
        liveByIdRef.current.set(evt.device_id, {
          ...prior,
          online,
          last_seen_ms: online ? Date.now() : prior.last_seen_ms,
        });
        rebuild();
        return;
      }
      if (evt.type !== 'device_telemetry') return;
      if (!armedSetRef.current.has(evt.device_id)) return;

      const enrollment = armedRowsRef.current.get(evt.device_id);
      if (!enrollment) return;
      const prior = liveByIdRef.current.get(evt.device_id);
      const gps = evt.gps;
      const hasFix = gps && gps.lat != null && gps.lon != null;
      const incomingSessionId = evt.session_id ?? null;
      // A session the runs list already marked not-recording can't come
      // back from an ordinary telemetry frame — only a genuinely different
      // id (a new recording) may replace it. Otherwise a stale phone-side
      // session_id, or an operator force-ending the session, would flip it
      // straight back to "live" on the phone's next frame.
      const resurrecting =
        incomingSessionId != null
        && endedSessionIdsRef.current.get(evt.device_id) === incomingSessionId;
      const next: EventLiveDevice = {
        device_id: evt.device_id,
        name: prior?.name ?? null,
        kind: 'phone',
        online: true,
        lat: hasFix ? (gps!.lat as number) : prior?.lat ?? 0,
        lon: hasFix ? (gps!.lon as number) : prior?.lon ?? 0,
        accuracy_m: hasFix ? gps!.accuracy_m ?? null : prior?.accuracy_m ?? null,
        speed_mps: hasFix ? gps!.speed_mps ?? null : prior?.speed_mps ?? null,
        heading_deg: hasFix ? gps!.heading_deg ?? null : prior?.heading_deg ?? null,
        fix_ts_ms: hasFix ? gps!.fix_ts_ms ?? null : prior?.fix_ts_ms ?? null,
        battery_level:
          normalizeBatteryFraction(evt.battery?.level) ?? prior?.battery_level ?? null,
        battery_charging: evt.battery?.charging ?? prior?.battery_charging ?? null,
        driver_name: enrollment.driver_name,
        vehicle: enrollment.vehicle,
        enrollment_id: enrollment.id,
        car_number: enrollment.car_number ?? null,
        // The backend always includes session_id on telemetry frames, so a
        // non-resurrecting value is authoritative — falling back to the
        // prior value turned every post-stop `null` into "still recording"
        // for the rest of the page lifetime.
        session_id: resurrecting ? null : incomingSessionId,
        last_seen_ms: Date.now(),
        // V2 IMU summary (1 s magnitude buckets); keep the prior value
        // through GPS-only frames so the G cell doesn't flicker.
        accel_peak_mps2:
          (typeof evt.imu?.accel_mag_peak_mps2 === 'number'
            ? evt.imu.accel_mag_peak_mps2
            : null) ?? prior?.accel_peak_mps2 ?? null,
        has_fix: Boolean(hasFix) || (prior?.has_fix ?? false),
      };
      // A fixless frame is still worth keeping: it carries liveness and
      // the recording session_id for a phone that has no GPS yet.
      liveByIdRef.current.set(evt.device_id, next);
      rebuild();
    });
    return () => unsub();
  }, [eventId, replay, rebuild]);

  const stableDevices = useMemo(() => devices, [devices]);

  return { event, devices: stableDevices, loading, error, replay: replay === true };
}
